import { createSelector } from "@reduxjs/toolkit";
import { BaseQueryFn } from "@reduxjs/toolkit/query";
import { TypedUseQueryStateResult } from "@reduxjs/toolkit/query/react";
import { BellIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import NotificationBody from "./NotificationBody";

import { TEN } from "@/constants/constants";
import { SOCKET_KEYS } from "@/constants/socket.constant";
import { useIntersectionCallback } from "@/hooks/useIntersectionCallback";
import useLocalPaginationHelper from "@/hooks/useLocalPaginationHelper";
import { useSocket } from "@/hooks/useSocket";
import { SocketData } from "@/interfaces/globals";
import { Notification } from "@/interfaces/interfaces";
import {
  generalApi,
  useGetInfiniteNotificationsInfiniteQuery,
  useReadLatestNotificationMutation,
} from "@/redux/api/generalQuery";
import { RootState } from "@/redux/store";
import { getWorkspace } from "@/utils/helpers";

type NotificationResult = TypedUseQueryStateResult<
  { pages: GeneralResponse<Notification[]>[] },
  unknown,
  BaseQueryFn
>;

const Notifications: React.FC = () => {
  const { unViewedNotifications } = useSelector(
    (state: RootState) => state.notification,
  );
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(unViewedNotifications);

  const dispatch = useDispatch();

  const selectCombinedNotifications = createSelector(
    (res: NotificationResult) => {
      return res?.data;
    },
    (data) => data?.pages?.map((page) => page?.data).flat(),
  );

  const { socket } = useSocket("/");
  const { localPagination } = useLocalPaginationHelper();

  const {
    combinedData = [],
    fetchNextPage,
    hasNextPage,
    ...rest
  } = useGetInfiniteNotificationsInfiniteQuery("", {
    selectFromResult: (result) => {
      return {
        ...result,
        combinedData: selectCombinedNotifications(result),
      };
    },
    initialPageParam: {
      offset: 0,
      limit: localPagination.limit ?? TEN,
    },
    skip: !open || !getWorkspace(),
  });
  const intersectionCallbackRef = useIntersectionCallback(() => {
    if (hasNextPage) {
      void fetchNextPage();
    }
  });

  const [readLatestMsg] = useReadLatestNotificationMutation();

  const handleReadAll = async (): Promise<void> => {
    if (!combinedData?.length) {
      return;
    }
    const latestMsg = combinedData?.reduce((latest, current) => {
      const latestDate = new Date(latest?.notification?.createdAt);
      const currentDate = new Date(current.notification.createdAt);

      return currentDate > latestDate ? current : latest;
    });

    await readLatestMsg({
      notificationId: latestMsg.notificationId,
    });
  };

  useEffect(() => {
    socket?.on(SOCKET_KEYS.NOTIFICATION, (data: SocketData) => {
      if (!open) {
        setCount((p) => p + 1);
      }
      if (data) {
        dispatch(generalApi.util.invalidateTags(["notification"]));
      }
    });

    return (): void => {
      socket?.off(SOCKET_KEYS.NOTIFICATION);
    };
  }, [socket, rest, open, dispatch]);

  useEffect(() => {
    if (unViewedNotifications) {
      setCount(unViewedNotifications);
    }
  }, [unViewedNotifications]);

  return (
    <div className='relative flex items-center' onClick={() => setOpen(true)}>
      <NotificationBody
        open={open}
        setOpen={setOpen}
        notifications={combinedData}
        isLoading={rest.isLoading}
        intersectionCallbackRef={intersectionCallbackRef}
        handleReadAll={handleReadAll}
        setCount={setCount}
      >
        <div className='relative'>
          {count > 0 ? (
            <div className='bg-primary absolute right-0 top-[-10px] font-medium text-[11px] text-white w-5 h-5  flex items-center justify-center rounded-full'>
              {count}
            </div>
          ) : null}
          <BellIcon size={25} strokeWidth={1.8} />
        </div>
      </NotificationBody>{" "}
    </div>
  );
};

export default Notifications;
